/**
 * Field events and summaries dashboard section: list with create/edit collapse.
 */

import * as React from 'react';
import {
  EVENTS_AND_SUMMARIES_TAB_COLORS as TAB_COLORS,
  EVENTS_AND_SUMMARIES_TAB_ICONS as TAB_ICONS,
  EVENTS_AND_SUMMARIES_TAB_LABELS as TAB_LABELS,
  type EventsAndSummariesTab,
} from '@/constants/shared-catalog';
import { useQueryClient } from '@tanstack/react-query';
import { EventsDataTable } from '@/components/events/EventsDataTable';
import { EventSummariesDataTable } from '@/components/event-summaries/EventSummariesDataTable';
import { CreateEventView } from '@/components/events/CreateEventView';
import { EditEventView } from '@/components/events/EditEventView';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { Select } from '@/components/forms/Select';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import { CalendarSyncModal } from '@/components/calendar/CalendarSyncModal';
import { CALENDAR_SYNC_ENABLED } from '@/config/features';
import { Button } from '@/components/forms/Button';
import { RefreshCw } from 'lucide-react';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link EventsAndSummariesSection}. */
interface EventsAndSummariesSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** Dashboard widget: events and summaries list with create/edit. */
export const EventsAndSummariesSection: React.FC<EventsAndSummariesSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const eventsActions = usePageActions('events');
  const summaryActions = usePageActions('event-summaries');
  const { canEditRow, canCancelRow, canManageAttendance, canExportCsv } = eventsActions;

  const [activeTab, setActiveTab] = React.useState<EventsAndSummariesTab>('events');
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editEventId, setEditEventId] = React.useState<string | null>(null);
  const queryClient = useQueryClient();
  const { openRowId, openRow, closeRow } = useDetailsView();
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const [syncOpen, setSyncOpen] = React.useState(false);

  function handleOpenSync(): void {
    setSyncOpen(true);
  }

  function handleCloseSync(): void {
    setSyncOpen(false);
  }

  function renderSyncButton(): React.ReactNode {
    if (!CALENDAR_SYNC_ENABLED) return undefined;
    return (
      <Button
        preset="section-action"
        icon={RefreshCw}
        tooltipText="Synchroniser"
        aria-label="Synchroniser"
        onClick={handleOpenSync}
      />
    );
  }

  /** Tabs allowed by view/edit actions; fall back to both if none apply. */
  const accessibleTabs = React.useMemo<EventsAndSummariesTab[]>(() => {
    const t: EventsAndSummariesTab[] = [];
    if (eventsActions.canViewRow || eventsActions.canEditRow) t.push('events');
    if (summaryActions.canViewRow || summaryActions.canEditRow) t.push('summaries');
    return t.length > 0 ? t : ['events', 'summaries'];
  }, [eventsActions, summaryActions]);

  React.useEffect(() => {
    if (!accessibleTabs.includes(activeTab)) {
      setActiveTab(accessibleTabs[0]);
    }
  }, [accessibleTabs, activeTab]);

  /** Close the form and drop the edit id so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditEventId(null);
  }, []);

  const handleEditEvent = (eventId: string) => {
    setEditEventId(eventId);
    setActiveForm('edit');
    closeRow();
  };

  /** Scroll the section to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  const formContent = React.useMemo(() => {
    if (activeForm === 'create') {
      return (
        <CreateEventView
          key="events-create"
          onClose={closeForm}
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ['events'], refetchType: 'all' });
            closeForm();
            onSuccess?.();
          }}
        />
      );
    }
    if (activeForm === 'edit' && editEventId) {
      return (
        <EditEventView
          key={`events-edit-${editEventId}`}
          eventId={editEventId}
          onClose={closeForm}
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ['events'], refetchType: 'all' });
            await queryClient.invalidateQueries({ queryKey: ['event'], refetchType: 'all' });
            closeForm();
            onSuccess?.();
          }}
        />
      );
    }
    return null;
  }, [activeForm, editEventId, queryClient, onSuccess, closeForm]);

  const tabItems = React.useMemo(
    () =>
      accessibleTabs.map((id) => {
        const Icon = TAB_ICONS[id];
        return {
          id,
          label: TAB_LABELS[id],
          icon: <Icon className="h-4 w-4" />,
          color: TAB_COLORS[id],
        };
      }),
    [accessibleTabs],
  );

  const tabsNode = React.useMemo(
    () =>
      accessibleTabs.length > 1 ? (
        <Select
          value={activeTab}
          onChange={(id) => {
            setActiveTab(id as typeof activeTab);
            closeForm();
            closeRow();
          }}
          options={tabItems.map((tab) => ({
            value: tab.id,
            label: tab.label,
            icon: tab.icon,
            color: tab.color,
          }))}
          size="sm"
        />
      ) : undefined,
    [accessibleTabs.length, tabItems, activeTab, closeRow],
  );

  return (
    <div ref={sectionRef}>
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        formContent={formContent ?? <></>}
      >
        {activeTab === 'events' ? (
          <EventsDataTable
            expandedRowId={openRowId}
            onToggleRow={openRow}
            onCloseDetails={closeRow}
            onEditEvent={handleEditEvent}
            layoutWidth={layoutWidth}
            isSection={true}
            toolbarLeft={tabsNode}
            toolbarExtra={renderSyncButton()}
            showCreateButton={eventsActions.canCreate}
            onCreateEvent={() => {
              closeRow();
              setActiveForm('create');
            }}
            pageActions={{
              canEditRow,
              canCancelRow,
              canManageAttendance,
              canExportCsv,
              canViewSummary: summaryActions.canViewRow,
              canEditSummary: summaryActions.canEditRow,
            }}
          />
        ) : (
          <EventSummariesDataTable
            expandedRowId={openRowId}
            onToggleRow={openRow}
            onCloseDetails={closeRow}
            layoutWidth={layoutWidth}
            isSection={true}
            toolbarLeft={tabsNode}
            toolbarExtra={renderSyncButton()}
            pageActions={{
              canEditRow: summaryActions.canEditRow,
              canViewRow: summaryActions.canViewRow,
              canExportCsv: summaryActions.canExportCsv,
            }}
          />
        )}
      </CollapseReplaceWrapper>
      {CALENDAR_SYNC_ENABLED ? (
        <CalendarSyncModal open={syncOpen} onClose={handleCloseSync} lockedTypes={['event']} />
      ) : null}
    </div>
  );
};
